import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mic, Paperclip, Pause, Play, Reply, Send, Square, Trash2, X } from 'lucide-react';

const formatSeconds = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

const RoundButton = ({ onClick, background, color, title, children, size = 40, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className="voice-btn"
    style={{
      '--vb-bg': background,
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: '50%',
      border: 'none',
      background,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      flexShrink: 0,
      boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {children}
  </button>
);

const ChatInput = ({
  messageInput,
  setMessageInput,
  handleSendMessage,
  handleTyping,
  handleKeyPress,
  handleFileSelect,
  isRecording,
  audioBlob,
  recordingTime,
  startRecording,
  stopRecording,
  sendRecording,
  cancelRecording,
  uploading,
  isConnected,
  fileInputRef,
  isMobile,
  styles,
  c,
  t,
  isRTL,
  theme,
  // Reply context (WhatsApp-style): quoted preview above the input.
  replyingTo = null,
  onCancelReply = () => {},
  selectedUserName = '',
  // Optional extra class on the textarea — see MessagesContainer's identical prop.
  textareaClassName = '',
}) => {
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const [sendingVoice, setSendingVoice] = useState(false);
  const previewAudioRef = useRef(null);

  // Object URL for previewing the recorded blob before sending.
  const previewUrl = useMemo(
    () => (audioBlob ? URL.createObjectURL(audioBlob) : null),
    [audioBlob]
  );

  useEffect(() => {
    setPreviewPlaying(false);
    setPreviewTime(0);
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const togglePreview = () => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (previewPlaying) {
      audio.pause();
      setPreviewPlaying(false);
    } else {
      audio.play().then(() => setPreviewPlaying(true)).catch(() => setPreviewPlaying(false));
    }
  };

  const handleSendVoice = async () => {
    if (sendingVoice) return;
    previewAudioRef.current?.pause();
    setPreviewPlaying(false);
    setSendingVoice(true);
    try {
      await sendRecording();
    } finally {
      setSendingVoice(false);
    }
  };

  const isDark = theme === 'dark';
  const pillStyle = {
    padding: '10px 14px',
    background: c.inputBg,
    borderRadius: '18px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    border: `1px solid ${c.border || 'rgba(0,0,0,0.08)'}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  };

  const replyPreviewText = replyingTo
    ? (replyingTo.text
        || replyingTo.mediaName
        || (replyingTo.type === 'image' ? '📷' : replyingTo.type === 'voice' ? '🎤' : ''))
    : '';

  return (
    <div style={styles.inputArea}>
      {/* ── Reply preview banner ── */}
      {replyingTo && (
        <div style={{ ...pillStyle, borderInlineStart: '4px solid #2e7cba' }}>
          <Reply size={18} color="#2e7cba" style={{ flexShrink: 0, transform: isRTL ? 'scaleX(-1)' : 'none' }} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '12.5px', fontWeight: '700', color: '#2e7cba' }}>
              {t.replyingTo} {replyingTo.sent ? t.you : (replyingTo.sender?.name || selectedUserName)}
            </span>
            <span dir="auto" style={{ fontSize: '13px', color: c.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {replyPreviewText}
            </span>
          </div>
          <button
            type="button"
            onClick={onCancelReply}
            title={t.cancel}
            aria-label={t.cancel}
            style={{
              border: 'none', background: 'transparent', color: c.textSecondary,
              cursor: 'pointer', display: 'flex', padding: '4px', flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── Recording in progress ── */}
      {isRecording && (
        <div style={pillStyle}>
          <div
            style={{
              width: '12px',
              height: '12px',
              background: '#ef4444',
              borderRadius: '50%',
              animation: 'pulse 1s infinite',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: '15px',
              fontWeight: '700',
              color: c.text,
              fontVariantNumeric: 'tabular-nums',
              direction: 'ltr',
              flexShrink: 0,
            }}
          >
            {formatSeconds(recordingTime)}
          </span>

          {/* Animated wave */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              height: '28px',
              overflow: 'hidden',
            }}
          >
            {[...Array(22)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: '3px',
                  height: '20px',
                  borderRadius: '2px',
                  background: '#2e7cba',
                  opacity: 0.85,
                  animation: `recWave 1.1s ease-in-out ${i * 0.07}s infinite`,
                }}
              />
            ))}
          </div>

          <RoundButton
            onClick={cancelRecording}
            background={isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2'}
            color="#ef4444"
            title={t.cancel}
          >
            <Trash2 size={18} />
          </RoundButton>
          <RoundButton
            onClick={stopRecording}
            background="linear-gradient(135deg, #ef4444, #dc2626)"
            color="#fff"
            title={t.stop}
          >
            <Square size={16} fill="currentColor" />
          </RoundButton>
        </div>
      )}

      {/* ── Recorded preview (ready to send) ── */}
      {!isRecording && audioBlob && (
        <div style={pillStyle}>
          <audio
            ref={previewAudioRef}
            src={previewUrl}
            onTimeUpdate={(e) => setPreviewTime(e.target.currentTime)}
            onEnded={() => {
              setPreviewPlaying(false);
              setPreviewTime(0);
            }}
          />

          <RoundButton
            onClick={togglePreview}
            background="linear-gradient(135deg, #2e7cba, #1a6baa)"
            color="#fff"
            title={previewPlaying ? t.stop : (isRTL ? 'استماع' : 'Play')}
          >
            {previewPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" style={{ marginLeft: '2px' }} />}
          </RoundButton>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <span style={{ fontSize: '13.5px', fontWeight: '700', color: c.text }}>
              {isRTL ? 'رسالة صوتية' : 'Voice message'}
            </span>
            <span
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: c.textSecondary,
                fontVariantNumeric: 'tabular-nums',
                direction: 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            >
              {previewPlaying || previewTime > 0
                ? `${formatSeconds(Math.floor(previewTime))} / ${formatSeconds(recordingTime)}`
                : formatSeconds(recordingTime)}
            </span>
          </div>

          <RoundButton
            onClick={cancelRecording}
            background={isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2'}
            color="#ef4444"
            title={t.cancel}
            disabled={sendingVoice}
          >
            <Trash2 size={18} />
          </RoundButton>
          <RoundButton
            onClick={handleSendVoice}
            background="linear-gradient(135deg, #22c55e, #16a34a)"
            color="#fff"
            title={t.send}
            size={44}
            disabled={sendingVoice}
          >
            {sendingVoice ? (
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            ) : (
              <Send size={18} style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
            )}
          </RoundButton>
        </div>
      )}

      {uploading && (
        <div
          style={{
            padding: '8px 16px',
            background: c.inputBg,
            borderRadius: '12px',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <div
            style={{
              width: '16px',
              height: '16px',
              border: '2px solid #2563eb',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <span style={{ fontSize: '14px', color: c.text }}>{t.uploading}</span>
        </div>
      )}

      <div style={styles.inputContainer}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.ms-powerpoint,.ppt,application/vnd.openxmlformats-officedocument.presentationml.presentation,.pptx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <button
          style={styles.iconButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || isRecording || audioBlob}
        >
          <Paperclip style={{ width: '20px', height: '20px' }} />
        </button>

        <button
          style={{
            ...styles.iconButton,
            color: isRecording ? '#ef4444' : '#2e7cba',
            background: isRecording ? (isDark ? 'rgba(239,68,68,0.15)' : '#fee2e2') : 'transparent',
          }}
          onClick={isRecording ? stopRecording : startRecording}
          disabled={uploading || audioBlob}
          title={isRecording ? t.stop : (isRTL ? 'تسجيل صوتي' : 'Record voice')}
        >
          <Mic style={{ width: '20px', height: '20px' }} />
        </button>

        <textarea
          value={messageInput}
          onChange={(e) => {
            setMessageInput(e.target.value);
            handleTyping();
          }}
          onKeyPress={handleKeyPress}
          placeholder={t.typeMessage}
          disabled={!isConnected || isRecording || audioBlob}
          className={textareaClassName}
          style={styles.messageInput}
          rows="1"
        />

        <button
          onClick={handleSendMessage}
          disabled={!messageInput.trim() || !isConnected || isRecording || audioBlob}
          style={{
            ...styles.sendButton,
            ...((!messageInput.trim() || !isConnected || isRecording || audioBlob) && styles.sendButtonDisabled),
          }}
        >
          <Send style={{ width: '20px', height: '20px' }} />
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
